function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/*!
 * @copyright
 * Copyright(c) 2022 SICK AG
 */

/**
 * Computes the number of fraction digits needed to represent the given value.
 * @param num the value to compute the fraction digits for.
 * @returns the number of fraction digits.
 */
export function getFractionDigits(num) {
  var _num$toString$split$;

  return ((_num$toString$split$ = num.toString(10).split('.')[1]) === null || _num$toString$split$ === void 0 ? void 0 : _num$toString$split$.length) || 0;
}
/**
 * NaN safe comparator to be used in @Prop options of ce-decorators based elements for number types.
 * @param oldValue previous value
 * @param newValue next value
 * @returns true, if values are unequal / have changed
 */

export const numberComparator = (oldValue, newValue) => {
  if (isNaN(oldValue) || isNaN(newValue)) {
    return isNaN(oldValue) !== isNaN(newValue);
  }

  return oldValue !== newValue;
};
/**
 * Treats testee as a number and returns it.
 * @param testee the value that should be treated as a number.
 * @returns testee as a number or NaN if testee cannot be treated as a number.
 */

/**
 * Treats testee as a number and returns it.
 * @param testee the value that should be treated as a number.
 * @param returnNull whether null should be returned if testee cannot be treated as a number.
 * @returns testee as number or NaN/null if testee cannot be treated as a number.
 */
export function numberGuard(testee, returnNull = false) {
  let result;

  if (typeof testee === 'number') {
    result = testee;
  } else if (testee instanceof Number) {
    result = testee.valueOf();
  } else {
    result = parseFloat(testee === null || testee === void 0 ? void 0 : testee.toString());
  }

  return returnNull && isNaN(result) ? null : result;
}
/**
 * Rounds the given value to the given number of fraction digits.
 * @param value the value to be rounded.
 * @param fractionDigits the number of fraction digits (must be >= 0).
 * @returns the given value rounded to the given number of fraction digits.
 */

export function roundToFraction(value, fractionDigits) {
  fractionDigits = Math.abs(Math.trunc(numberGuard(fractionDigits) || 0));
  value = numberGuard(value) || 0;
  value = value * 10 ** fractionDigits;
  value = Math.round(value);
  return value / 10 ** fractionDigits;
}
/**
 * A utility class that supports incrementing and decrementing values in accordance with certain boundary conditions.
 */

export class NumberStepper {
  /**
   * Whether the value should be aligned to the step-value when incremented or decremented.
   * The default is true.
   * @see step
   * @default true
   */

  /**
   * The greatest supported value.
   * The default is NaN which implies no limit at all.
   * @default NaN
   */
  get max() {
    return this._max;
  }

  set max(v) {
    this._max = numberGuard(v);
  }
  /**
   * The smallest supported value.
   * The default is NaN which implies no limit at all.
   * @default NaN
   */


  get min() {
    return this._min;
  }

  set min(v) {
    this._min = numberGuard(v);

    this._setFractionDigits();
  }
  /**
   * Whether the value should be rounded when incremented or decremented.
   * The default is true.
   * @default true
   */


  /**
   * If a value is incremented, this is the value it is incremented by.
   * If a value is decremented, this is the value it is decremented by.
   * The default is 1.
   * @default 1
   */
  get step() {
    return this._step;
  }

  set step(v) {
    this._step = numberGuard(v) || 1;

    this._setFractionDigits();
  }

  /**
   * Creates a new {@link NumberStepper}
   * @param min the smallest value to be supported by the new {@link NumberStepper}
   * @param max the greatest value to be supported by the new {@link NumberStepper}
   * @param step the step of the new {@link NumberStepper}
   */
  constructor(min, max, step) {
    _defineProperty(this, "alignToStep", true);

    _defineProperty(this, "round", true);

    _defineProperty(this, "_fractionDigits", 0);

    _defineProperty(this, "_max", NaN);

    _defineProperty(this, "_min", NaN);

    _defineProperty(this, "_step", 1);

    this.min = min;
    this.max = max;
    this.step = step;
  }
  /**
   * Applies the configuration of this {@link NumberStepper} to the given value.
   * @see min
   * @see max
   */


  applyTo(value) {
    return this._applyMinMax(numberGuard(value));
  }
  /**
   * Decreases the given value by {@link step}.
   */


  decrease(value) {
    value = numberGuard(value);

    if (isNaN(value) || this.min > this.max) {
      return value;
    }

    const valueAligned = this._alignToStep(value);

    const steppedValue = value - this.step;

    const steppedValueAligned = this._alignToStep(steppedValue);

    if (valueAligned === value) {
      value = steppedValue;
    } else if (valueAligned < value) {
      value = valueAligned;
    } else if (valueAligned > value) {
      value = steppedValueAligned;
    }

    return this._round(this._applyMinMax(value));
  }
  /**
   * Increases the given value by {@link step}.
   */


  increase(value) {
    value = numberGuard(value);

    if (isNaN(value) || this.min > this.max) {
      return value;
    }

    const valueAligned = this._alignToStep(value);

    const steppedValue = value + this.step;

    const steppedValueAligned = this._alignToStep(steppedValue);

    if (valueAligned === value) {
      value = steppedValue;
    } else if (valueAligned > value) {
      value = valueAligned;
    } else if (valueAligned < value) {
      value = steppedValueAligned;
    }

    return this._round(this._applyMinMax(value));
  }

  _alignToStep(value) {
    if (!this.alignToStep) {
      return value;
    }

    const base = numberGuard(this.min) || 0;
    return this._round(Math.round((value - base) / this.step) * this.step + base);
  }

  _applyMinMax(value) {
    if (this.min > this.max) {
      return value;
    }

    if (value < this.min) {
      value = this.min;
    }

    if (value > this.max) {
      value = this.max;
    }

    return value;
  }

  _round(value) {
    return this.round || this.alignToStep // alignToStep only correctly works WITH round => override, if round == false
    ? roundToFraction(value, this._fractionDigits) : value;
  }

  _setFractionDigits() {
    this._fractionDigits = Math.max(getFractionDigits(this.step), getFractionDigits(this.min));
  }

}
